import React, { useState, useEffect, useCallback } from "react";
import { Col, Row } from "antd";
import { Form, SubmitButton, Select, Input } from "formik-antd";
import { ColorResult, TwitterPicker } from "react-color";
import { hueService, connectionService } from "/app/src/services";
import { IconToolTip } from "/app/src/components/icons/IconBuilder";
import { useTranslation } from "react-i18next";
import { WorkflowAction, Connection } from "/app/src/models";
import { buildParams } from "/app/src/helpers/params";
import { Button } from "@heroui/react";
import { useFormikContext } from "formik";
import { useQuery } from "@tanstack/react-query";

type Zone = {
  id: number;
  name: string;
};

interface FormValues {
  to: string | undefined;
  value: string | undefined;
  message: string | undefined;
  connectionId: number | undefined;
}

/*
 * Form for the Hue action
 */
export default function HueForm({
  action,
  removeWorkflowAction,
  isNew,
}: {
  action: WorkflowAction;
  removeWorkflowAction: (
    action: WorkflowAction,
  ) => Promise<{ deleted: boolean }> | undefined;
  isNew: boolean;
}) {
  const { dirty, setFieldValue, values } = useFormikContext<FormValues>();
  const [color, setColor] = useState<string>("#fcdc00");
  const [showing, setShowing] = useState(false);
  const { data: zones } = useQuery({
    queryKey: ["zones"],
    queryFn: () => {
      return hueService.getAllZones({ id: values?.connectionId });
    },
    initialData: { zones: [] },
    select: (data: { zones }) => {
      return data.zones;
    },
    enabled: Boolean(values?.connectionId),
  });
  const { t } = useTranslation();

  //Hide the color picker
  const hidePicker = useCallback(() => {
    setShowing((prevShowing) => !prevShowing);
  }, []);

  const handleRemoveWorkflowAction = useCallback(() => {
    removeWorkflowAction(action);
  }, [action, removeWorkflowAction]);

  const handleColorChange = useCallback(
    (colour: ColorResult) => {
      if (colour) {
        setColor(colour.hex);
        setFieldValue("message", colour.hex);
      }
      setShowing(false);
    },
    [setFieldValue],
  );

  const handleBridgeChange = useCallback(() => {
    setFieldValue("value", "");
  }, [setFieldValue]);

  const handleToChange = useCallback(() => {
    setShowing(false);
  }, []);

  const { data: connections } = useQuery({
    queryKey: ["connections", { type: "[or]Hue Remote" }],
    queryFn: () => {
      return connectionService.getAll(
        buildParams({
          type: "Hue Remote",
        }),
      );
    },
    select: (data: { connections: Connection[] }) => {
      return data.connections;
    },
    initialData: { connections: [] },
  });

  useEffect(() => {
    if (isNew === false && action?.message) {
      setColor(String(action.message));
    }
  }, [isNew, action]);

  const learnMore = useCallback(() => {
    const isNewWindow = window.open(
      "https://itempath.com/docs/phillips-hue/setting-up-a-zone",
      "_blank",
      "noopener,noreferrer",
    );
    if (isNewWindow) isNewWindow.opener = null;
  }, []);
  return (
    <Row justify="start" gutter={16}>
      <Col span={4}>
        <span className="blockText">{t("translation:activate")}</span>
      </Col>
      <Col span={6}>
        <Form.Item name="connectionId">
          <Select
            name="connectionId"
            size="large"
            placeholder={t("translation:select_bridge")}
            onChange={handleBridgeChange}
          >
            {connections.map((c: Connection) => (
              <Select.Option key={c.id} value={c.id}>
                {c.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        {Boolean(values?.connectionId) && zones.length < 1 ? (
          <IconToolTip
            content={t("translation:no_hue_zones_found")}
            icon="Warning"
            onClick={learnMore}
            color="#ffcc00"
            size={32}
          />
        ) : (
          ""
        )}
        <Form.Item name="value">
          <Select
            name="value"
            size="large"
            placeholder={t("translation:select_zone")}
          >
            {zones.map((c: Zone) => (
              <Select.Option key={c.id} value={c.id}>
                {c.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="message">
          <Input.TextArea name="message" placeholder="" hidden value={color} />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item name="to">
          <Select
            name="to"
            size="large"
            placeholder={t("translation:select_type")}
            onChange={handleToChange}
          >
            <Select.Option value="On">
              {t("translation:turn_lights_on")}
            </Select.Option>
            <Select.Option value="Off">
              {t("translation:turn_lights_off")}
            </Select.Option>
            <Select.Option value="Alert">
              {t("translation:alert_lights")}
            </Select.Option>
          </Select>
        </Form.Item>
        {["On", "Alert"].includes(values.to) && (
          <div style={{ backgroundColor: `${color}` }}>
            <Button
              size="md"
              radius="none"
              variant="solid"
              color="primary"
              fullWidth
              onPress={hidePicker}
              className={`bg-[${color}]`}
            >
              {t("translation:select_colour")}
            </Button>
          </div>
        )}
        {showing ? (
          <div className="w-full">
            <TwitterPicker
              width="100%"
              onChangeComplete={handleColorChange}
              triangle="top-right"
              color={color}
              colors={[
                "#FF6900",
                "#FCB900",
                "#7BDCB5",
                "#00D084",
                "#8ED1FC",
                "#0693E3",
                "#ABB8C3",
                "#EB144C",
                "#F78DA7",
                "#9900EF",
                "#FF4444",
                "#FFD700",
                "#2ECC71",
                "#1ABC9C",
                "#3498DB",
                "#9B59B6",
                "#34495E",
                "#E74C3C",
              ]}
            />
          </div>
        ) : null}
      </Col>

      <Col flex="auto">
        <SubmitButton type="primary" size="large" block disabled={!dirty}>
          {isNew ? t("translation:add_action") : t("translation:save")}
        </SubmitButton>
      </Col>
      {!isNew && (
        <Col span={3}>
          <Button
            size="md"
            radius="none"
            variant="bordered"
            color="default"
            fullWidth
            onPress={handleRemoveWorkflowAction}
            className="hover:border-primary-default hover:text-primary-default bg-white"
          >
            {t("translation:remove")}
          </Button>
        </Col>
      )}
    </Row>
  );
}
